import { createContext, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { sessionViewPath } from '@kirin/api-client/helpers/alerts';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';

/**
 * KIR-4785: set by the Session view around the Alert Variations modal it opens,
 * so the entry points inside that modal know which session is already open.
 * Null everywhere else.
 */
export interface OpenSessionView {
  sessionId: string;
  occurrenceId: string;
  /** The Alerts table's search string the Session view was opened with. */
  alertsSearch: string;
}

export const OpenSessionViewContext = createContext<OpenSessionView | null>(
  null
);

/**
 * True inside the pop-out alert window (`AlertWindowPage`), which has no app
 * layout: the Session view opens in a new tab there instead of in the pop-out.
 */
export const AlertWindowContext = createContext(false);

/** Both `alert_session_view` and `agent_session_timeline` are on. */
export function useSessionViewEnabled(): boolean {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.ALERT_SESSION_VIEW,
    flagsmithFeatureFlags.AGENT_SESSION_TIMELINE,
  ]);
  return (
    flags[flagsmithFeatureFlags.ALERT_SESSION_VIEW] === true &&
    flags[flagsmithFeatureFlags.AGENT_SESSION_TIMELINE] === true
  );
}

/** True for the session the Session view already shows. */
export function useIsOpenSession(sessionId: string | undefined): boolean {
  const open = useContext(OpenSessionViewContext);
  return sessionId !== undefined && open?.sessionId === sessionId;
}

/**
 * Both entry points (the Session view button and the Session ID menu) navigate
 * through here; mount it only behind {@link useSessionViewEnabled}. From the
 * Alerts table the current search string rides along, so Back restores the
 * table. From inside the Session view the original search is kept and history
 * is replaced, so Back to Alerts still lands on the starting table. From the
 * pop-out alert window it opens in a new tab, with no Alerts search.
 */
export function useOpenSessionView(
  organizationId: string | null | undefined
): (occurrenceId: string) => void {
  const open = useContext(OpenSessionViewContext);
  const inAlertWindow = useContext(AlertWindowContext);
  const navigate = useNavigate();
  const location = useLocation();
  return occurrenceId => {
    if (inAlertWindow) {
      window.open(
        sessionViewPath(organizationId, occurrenceId),
        '_blank',
        'noopener,noreferrer'
      );
      return;
    }
    navigate(
      {
        pathname: sessionViewPath(organizationId, occurrenceId),
        search: open ? open.alertsSearch : location.search,
      },
      { replace: open !== null }
    );
  };
}
